//
// This code is performance sensitive!
// -----------------------------------
// Currently the id-dispenser makes use of the graph.
// Changes to the graph creation can impact the response time.
//
const { filterMapLocationNodes } = require('./filter')

function sanitizeId (id) {
  if (id.startsWith('country:')) {
    return id
  } else if (id === 'national') {
    return 'country:ng'
  } else {
    return `country:ng:${id}`
  }
}

function idToParts (locationId) {
  if (locationId === 'national') {
    return ['country', 'ng']
  }
  let idParts = locationId.split(':')
  if (idParts[0] !== 'country') {
    idParts = ['country', 'ng', ...idParts]
  }
  return idParts
}

function partsWithoutName (idParts) {
  const parts = []
  for (let i = 0; i < idParts.length; i += 2) {
    if (idParts[i] === 'name' || idParts[i] === 'sdp') {
      break
    }
    parts.push(idParts[i], idParts[i + 1])
  }
  return parts
}

class Graph {
  constructor (locations) {
    this.createNodes(locations)
  }

  createNodes (locations) {
    const locationNodes = new Map()
    const geoNodes = new Map()

    for (const location of locations) {
      const idParts = idToParts(location._id)
      const nodeId = idParts.join(':')
      const locationNode = {
        id: nodeId,
        location,
        supplies: new Set(),
        suppliedBy: new Set()
      }
      locationNodes.set(nodeId, locationNode)

      let parentGeoNode = null
      const geoParts = partsWithoutName(idParts)
      for (let i = 0; i < geoParts.length; i += 2) {
        const currParts = geoParts.slice(0, i + 2)
        const geoId = currParts.join(':')
        let geoNode = geoNodes.get(geoId)
        if (!geoNode) {
          geoNode = {
            id: geoId,
            locations: new Set(),
            parent: parentGeoNode,
            children: new Set()
          }
          geoNodes.set(geoId, geoNode)
        }
        if (parentGeoNode) {
          parentGeoNode.children.add(geoNode)
        }
        if (currParts.length === geoParts.length) {
          geoNode.locations.add(locationNode)
        }
        parentGeoNode = geoNode
      }
    }

    for (const locationNode of locationNodes.values()) {
      const location = locationNode.location

      let relationships = location.relationships || {}
      if (!relationships || Object.keys(relationships).length === 0) {
        const idParts = idToParts(location._id)
        const geoParts = partsWithoutName(idParts)

        for (let i = geoParts.length - 2; i >= 0; i -= 2) {
          const supplierId = geoParts.slice(0, i).join(':')
          const supplierNode = locationNodes.get(supplierId)
          if (supplierNode) {
            locationNode.suppliedBy.add(supplierNode)
            supplierNode.supplies.add(locationNode)
            break
          }
        }
      } else {
        if (relationships.suppliedBy) {
          for (const relId of relationships.suppliedBy) {
            const supplierId = sanitizeId(relId)
            const supplierNode = locationNodes.get(supplierId)
            if (supplierNode) {
              locationNode.suppliedBy.add(supplierNode)
              supplierNode.supplies.add(locationNode)
            }
          }
        }
        if (relationships.supplies) {
          for (const relId of relationships.supplies) {
            const supplieeId = sanitizeId(relId)
            const supplieeNode = locationNodes.get(supplieeId)
            if (supplieeNode) {
              locationNode.supplies.add(supplieeNode)
              supplieeNode.suppliedBy.add(locationNode)
            }
          }
        }
      }
      const relationshipRules = locationNode.location.relationshipRules || {}
      if (relationshipRules.suppliesStateSDPs) {
        const stateIds = relationshipRules.suppliesStateSDPs
        for (const stateId of stateIds) {
          const geoNode = geoNodes.get(sanitizeId(stateId))
          if (!geoNode) {
            continue
          }
          let toVisit = [...geoNode.children.keys()]
          while (toVisit.length > 0) {
            const nextVisit = []
            for (const childGeoNode of toVisit) {
              for (const childLocationNode of childGeoNode.locations) {
                locationNode.supplies.add(childLocationNode)
                childLocationNode.suppliedBy.add(locationNode)
              }
              nextVisit.push(...childGeoNode.children.keys())
            }
            toVisit = nextVisit
          }
        }
      }
    }
    this.locationNodes = locationNodes
    this.geoNodes = geoNodes
  }

  hasLocation (id) {
    const nodeId = sanitizeId(id)
    return this.locationNodes.has(nodeId)
  }

  getLocation (id) {
    const nodeId = sanitizeId(id)
    const node = this.locationNodes.get(nodeId)
    if (!node) {
      return null
    }
    return node.location
  }

  getLocationIds () {
    const ids = []
    for (const node of this.locationNodes) {
      ids.push(node.location._id)
    }
    return ids
  }

  getSuppliers (id, options) {
    const nodeId = sanitizeId(id)
    const node = this.locationNodes.get(nodeId)
    if (!node) {
      return []
    }
    return filterMapLocationNodes(node.suppliedBy.keys(), options)
  }

  getSuppliees (id, options) {
    const nodeId = sanitizeId(id)
    const node = this.locationNodes.get(nodeId)
    if (!node) {
      return []
    }
    return filterMapLocationNodes(node.supplies.keys(), options)
  }

  getSiblings (id, options) {
    const nodeId = sanitizeId(id)
    const node = this.locationNodes.get(nodeId)
    if (!node) {
      return []
    }
    const siblings = []
    for (const supplier of node.suppliedBy.keys()) {
      for (const sibling of supplier.supplies.keys()) {
        if (sibling.id !== node.id && sibling.location.level === node.location.level) {
          siblings.push(sibling)
        }
      }
    }
    return filterMapLocationNodes(siblings, options)
  }

  getDescendantSuppliers (id, options) {
    return this.getDescendantForIds([id], 'suppliedBy', options)
  }

  getDescendantSuppliersForIds (ids, options) {
    return this.getDescendantForIds(ids, 'suppliedBy', options)
  }

  getDescendantSuppliees (id, options) {
    return this.getDescendantForIds([id], 'supplies', options)
  }

  getDescendantForIds (ids, descendantKey, options) {
    let toVisit = []
    for (const id of ids) {
      const nodeId = sanitizeId(id)
      const node = this.locationNodes.get(nodeId)
      if (node) {
        toVisit.push(...node[descendantKey].keys())
      }
    }
    const all = new Set()
    while (toVisit.length > 0) {
      const nextVisit = []
      for (const d of toVisit) {
        if (all.has(d)) {
          continue
        }
        all.add(d)
        nextVisit.push(...d[descendantKey].keys())
      }
      toVisit = nextVisit
    }
    return filterMapLocationNodes(all, options)
  }

  getLocationsInGeoLocation (id, options) {
    const idParts = idToParts(id)
    const geoId = partsWithoutName(idParts).join(':')
    const geoNode = this.geoNodes.get(geoId)
    if (!geoNode) {
      return []
    }
    const allLocationNodes = new Set(geoNode.locations.keys())
    let toVisit = [...geoNode.children.keys()]
    while (toVisit.length > 0) {
      const nextVisit = []
      for (const childGeoNode of toVisit) {
        for (const childLocationNode of childGeoNode.locations) {
          allLocationNodes.add(childLocationNode)
        }
        nextVisit.push(...childGeoNode.children.keys())
      }
      toVisit = nextVisit
    }
    return filterMapLocationNodes(allLocationNodes, options)
  }
}

module.exports = {
  Graph
}
